/**
 * Every callable the apps can invoke: its name, who may call it, what it takes
 * and, where an app reads it, what it returns.
 *
 * The functions register each callable through this table, so parsing and the
 * access check happen in one place before any handler runs. The apps call
 * through it too, so a payload that drifts from what the function expects is a
 * type error rather than an `invalid-argument` somebody finds in production.
 */

import { assertBirr } from "./money.js";
import { isPeriod, type Period } from "./period.js";
import { ENTITLEMENTS, type Entitlement, entitlementsOf } from "./plans.js";
import { TELEBIRR_RETURNS, type TelebirrOrderStatus } from "./telebirr.js";
import { REMINDER_NOTE_LIMIT } from "./messages.js";
import { CHAPA_RETURNS, type ChapaOrderStatus, type ChapaReceiptDetail } from "./chapa.js";

/** A payload that does not match the contract. The functions answer `invalid-argument`. */
export class ContractError extends Error {}

type Parse<T> = (value: unknown, field: string) => T;

export interface Field<T, Optional extends boolean = boolean> {
  parse: Parse<T>;
  optional: Optional;
}

const required = <T>(parse: Parse<T>): Field<T, false> => ({ parse, optional: false });
const optional = <T>(parse: Parse<T>): Field<T, true> => ({
  parse: (value, field) => parse(value, field),
  optional: true,
});

/* ------------------------------------------------------------------ parsers */

const text: Parse<string> = (value, field) => {
  if (typeof value !== "string" || value.trim() === "") {
    throw new ContractError(`${field} is required.`);
  }
  return value.trim();
};

/** Free text that may be empty, trimmed. */
const note: Parse<string> = (value, field) => {
  if (typeof value !== "string") {
    throw new ContractError(`${field} must be text.`);
  }
  return value.trim();
};

/** Free text up to the reminder note's limit, trimmed; empty is allowed. */
const limitedNote: Parse<string> = (value, field) => {
  const said = note(value, field);
  if (said.length > REMINDER_NOTE_LIMIT) {
    throw new ContractError(`${field} is ${String(REMINDER_NOTE_LIMIT)} characters at most.`);
  }
  return said;
};

/**
 * Every override carries one. A change nobody can explain later is a change the
 * committee has to take on trust, which is the thing this system exists to
 * replace.
 */
const reason: Parse<string> = (value) => {
  if (typeof value !== "string" || value.trim() === "") {
    throw new ContractError("A reason is required.");
  }
  return value.trim();
};

/** Whole birr, either sign: adjustments may be negative. */
const birr: Parse<number> = (value, field) => {
  if (typeof value !== "number") {
    throw new ContractError(`${field} must be a whole number of birr.`);
  }
  try {
    assertBirr(value, field);
  } catch {
    throw new ContractError(`${field} must be a whole number of birr.`);
  }
  return value;
};

const whole: Parse<number> = (value, field) => {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new ContractError(`${field} must be a whole number.`);
  }
  return value;
};

const flag: Parse<boolean> = (value, field) => {
  if (typeof value !== "boolean") {
    throw new ContractError(`${field} must be true or false.`);
  }
  return value;
};

const period: Parse<Period> = (value, field) => {
  if (typeof value !== "string" || !isPeriod(value)) {
    throw new ContractError(`"${String(value)}" is not a billing period (${field}).`);
  }
  return value;
};

/** An ISO date, kept as text so it crosses the wire unchanged. */
const date: Parse<string> = (value, field) => {
  if (typeof value !== "string" || Number.isNaN(Date.parse(value))) {
    throw new ContractError(`"${String(value)}" is not a date (${field}).`);
  }
  return value;
};

function oneOf<const T extends readonly string[]>(values: T): Parse<T[number]> {
  return (value, field) => {
    if (typeof value !== "string" || !values.includes(value)) {
      throw new ContractError(`${field} must be one of ${values.join(", ")}.`);
    }
    return value as T[number];
  };
}

function list<T>(item: Parse<T>, limits: { min?: number; max: number }): Parse<T[]> {
  return (value, field) => {
    if (!Array.isArray(value)) {
      throw new ContractError(`${field} must be a list.`);
    }
    if (value.length < (limits.min ?? 0)) {
      throw new ContractError(`${field} needs at least ${String(limits.min)}.`);
    }
    if (value.length > limits.max) {
      throw new ContractError(`${String(limits.max)} is the most ${field} can hold at once.`);
    }
    return value.map((entry, index) => item(entry, `${field}[${String(index)}]`));
  };
}

/* ------------------------------------------------------------ shared values */

export const HELD_RESOLUTIONS = ["refunded", "waived", "extended"] as const;
export type HeldResolution = (typeof HELD_RESOLUTIONS)[number];

export const EXPENSE_CATEGORIES = [
  "maintenance",
  "utilities",
  "security",
  "cleaning",
  "salaries",
  "government",
  "other",
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const STAFF_ROLES = ["accountant", "committee"] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

export const ORGANIZATION_STATUSES = ["pending", "active", "suspended"] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

/** The most units one reminder run may reach. */
export const REMINDER_LIMIT = 200;

const newUnit: Parse<{ unitId: string; block: string; floor: number }> = (value, field) => {
  if (typeof value !== "object" || value === null) {
    throw new ContractError(`${field} must be { unitId, block, floor }.`);
  }
  const unit = value as Record<string, unknown>;
  return {
    unitId: text(unit.unitId, `${field}.unitId`),
    block: text(unit.block, `${field}.block`),
    floor: whole(unit.floor, `${field}.floor`),
  };
};

/* ------------------------------------------------------------------- access */

/**
 * Who may call. `accountant` and `committee` are that role in the complex named
 * by the payload's `orgId`; `staff` is the accountant or a platform admin
 * reaching in; `support` is those or the committee, for helping a resident who
 * is locked out; `resident` is a resident of that complex acting for their own
 * unit; `platformAdmin` is ours alone; `self` is any signed-in account acting
 * on itself.
 */
export type Access =
  "accountant" | "committee" | "staff" | "support" | "resident" | "platformAdmin" | "self";

/** Accesses whose payload names a complex, checked against the caller's token. */
export const ORG_SCOPED: readonly Access[] = [
  "accountant",
  "committee",
  "staff",
  "support",
  "resident",
];

const organization = { orgId: required(text) };

export const CALLABLES = {
  // Money, the accountant's.
  raiseInvoices: { access: "accountant", input: { ...organization, period: optional(period) } },
  verifyPaymentCall: {
    access: "accountant",
    input: { ...organization, paymentId: required(text) },
  },
  rejectPaymentCall: {
    access: "accountant",
    input: { ...organization, paymentId: required(text), reason: required(reason) },
  },
  postAdjustmentCall: {
    access: "accountant",
    input: {
      ...organization,
      unitId: required(text),
      period: optional(period),
      amount: required(birr),
      reason: required(reason),
    },
  },
  changeBuildingCall: {
    access: "accountant",
    input: {
      ...organization,
      buildingId: required(text),
      reason: required(reason),
      name: optional(note),
      address: optional(note),
      defaultMonthlyDue: optional(birr),
      dueDayOfMonth: optional(whole),
      autoReminders: optional(flag),
    },
  },
  changeUnitCall: {
    access: "accountant",
    input: {
      ...organization,
      unitId: required(text),
      reason: required(reason),
      monthlyDue: optional(birr),
      billingPaused: optional(flag),
    },
  },
  recordPaymentCall: {
    access: "accountant",
    input: {
      ...organization,
      unitId: required(text),
      amount: required(birr),
      reason: required(reason),
      method: optional(text),
    },
  },
  reversePaymentCall: {
    access: "accountant",
    input: {
      ...organization,
      paymentId: required(text),
      reason: required(reason),
      to: optional(oneOf(["submitted", "rejected"] as const)),
    },
  },
  voidInvoiceCall: {
    access: "accountant",
    input: {
      ...organization,
      unitId: required(text),
      period: optional(period),
      reason: required(reason),
    },
  },
  resolveHeldFundsCall: {
    access: "accountant",
    input: {
      ...organization,
      heldId: required(text),
      resolution: required(oneOf(HELD_RESOLUTIONS)),
      reason: required(reason),
    },
  },
  recordExpenseCall: {
    access: "accountant",
    input: {
      ...organization,
      amount: required(birr),
      category: required(oneOf(EXPENSE_CATEGORIES)),
      vendor: required(text),
      reason: required(reason),
      spentAt: optional(date),
      accountId: optional(text),
    },
  },
  voidExpenseCall: {
    access: "accountant",
    input: { ...organization, expenseId: required(text), reason: required(reason) },
  },
  setAccountBalanceCall: {
    access: "accountant",
    input: {
      ...organization,
      accountId: required(text),
      balance: required(birr),
      reason: required(reason),
      name: optional(text),
    },
  },
  remindUnitsCall: {
    access: "accountant",
    needs: "reminders",
    input: {
      ...organization,
      unitIds: required(list(text, { min: 1, max: REMINDER_LIMIT })),
      note: optional(limitedNote),
    },
  },

  // Campaigns, the committee's.
  createCampaignCall: {
    access: "committee",
    needs: "campaigns",
    input: {
      ...organization,
      title: required(text),
      target: required(birr),
      purpose: optional(note),
      closesAt: optional(date),
    },
  },
  closeCampaignCall: {
    access: "committee",
    input: { ...organization, campaignId: required(text), reason: required(reason) },
  },

  // Accounts.
  completeFirstLoginCall: {
    access: "self",
    input: { name: optional(text), phone: optional(text) },
  },
  createResidentCall: { access: "staff", input: { ...organization, unitId: required(text) } },
  handOverUnitCall: {
    access: "staff",
    input: { ...organization, unitId: required(text), reason: required(reason) },
  },
  resetPasswordCall: {
    access: "support",
    input: { ...organization, loginId: required(text), reason: required(reason) },
  },
  addUnitsCall: {
    access: "staff",
    input: { ...organization, units: required(list(newUnit, { min: 1, max: 400 })) },
  },
  vouchForResidentCall: {
    access: "staff",
    input: { ...organization, unitId: required(text), reason: required(reason) },
  },
  issueSlipsCall: {
    access: "staff",
    input: { ...organization, unitIds: required(list(text, { min: 1, max: 500 })) },
  },
  setAccountActiveCall: {
    access: "staff",
    input: {
      ...organization,
      uid: required(text),
      active: required(flag),
      reason: required(reason),
    },
  },

  // Complexes, ours.
  createOrganizationCall: {
    access: "platformAdmin",
    input: {
      code: required(text),
      name: required(text),
      city: optional(note),
      buildingName: required(text),
      address: optional(note),
      defaultMonthlyDue: required(birr),
      dueDayOfMonth: required(whole),
    },
  },
  createComplexCall: {
    access: "platformAdmin",
    input: {
      realEstateName: required(text),
      name: required(text),
      city: optional(note),
      buildingName: required(text),
      address: optional(note),
      defaultMonthlyDue: required(birr),
      dueDayOfMonth: required(whole),
    },
  },
  setOrganizationStatusCall: {
    access: "platformAdmin",
    input: {
      orgId: required(text),
      status: required(oneOf(ORGANIZATION_STATUSES)),
      reason: required(reason),
    },
  },
  createStaffCall: {
    access: "platformAdmin",
    input: {
      orgId: required(text),
      loginId: required(text),
      appRole: required(oneOf(STAFF_ROLES)),
    },
  },
  // Telebirr: the resident's, and the merchant account, ours.
  startTelebirrPaymentCall: {
    access: "resident",
    needs: "telebirr",
    input: {
      ...organization,
      amount: required(birr),
      monthsAhead: optional(whole),
      returnTo: required(oneOf(TELEBIRR_RETURNS)),
    },
  },
  checkTelebirrPaymentCall: {
    access: "resident",
    needs: "telebirr",
    input: { ...organization, orderId: required(text) },
  },
  setTelebirrMerchantCall: {
    access: "platformAdmin",
    input: {
      orgId: required(text),
      enabled: required(flag),
      environment: optional(oneOf(["testbed", "production"] as const)),
      fabricAppId: optional(text),
      appSecret: optional(text),
      merchantAppId: optional(text),
      shortCode: optional(text),
      privateKey: optional(text),
      telebirrPublicKey: optional(text),
      reason: required(reason),
    },
  },

  // Chapa: the resident's, and the committee's subaccount, ours.
  startChapaPaymentCall: {
    access: "resident",
    input: {
      ...organization,
      amount: required(birr),
      monthsAhead: optional(whole),
      returnTo: optional(oneOf(CHAPA_RETURNS)),
    },
  },
  checkChapaPaymentCall: {
    access: "resident",
    input: { ...organization, orderId: required(text) },
  },
  /** Reads only: asks Chapa about one payment, for its receipt. */
  chapaReceiptCall: {
    access: "accountant",
    input: { ...organization, paymentId: required(text) },
  },
  setChapaSubaccountCall: {
    access: "platformAdmin",
    input: {
      orgId: required(text),
      enabled: required(flag),
      subaccountId: optional(text),
      reason: required(reason),
    },
  },

  // Plans, ours.
  savePlanCall: {
    access: "platformAdmin",
    input: {
      planId: optional(text),
      name: required(text),
      monthlyPrice: required(birr),
      unitLimit: optional(whole),
      entitlements: required(list(oneOf(ENTITLEMENTS), { max: ENTITLEMENTS.length })),
    },
  },
  setSubscriptionCall: {
    access: "platformAdmin",
    input: {
      orgId: required(text),
      planId: required(text),
      paidUntil: optional(date),
      reason: required(reason),
    },
  },
} as const satisfies Record<
  string,
  { access: Access; input: Record<string, Field<unknown>>; needs?: Entitlement }
>;

export type CallableName = keyof typeof CALLABLES;

type Schema<N extends CallableName> = (typeof CALLABLES)[N]["input"];
type Value<F> = F extends Field<infer T> ? T : never;

/** What a callable takes, with optional fields optional. */
export type InputOf<N extends CallableName> = {
  [K in keyof Schema<N> as Schema<N>[K] extends Field<unknown, false> ? K : never]: Value<
    Schema<N>[K]
  >;
} & {
  [K in keyof Schema<N> as Schema<N>[K] extends Field<unknown, true> ? K : never]?: Value<
    Schema<N>[K]
  >;
};

/** Checks a payload against the contract and returns it trimmed and typed. */
export function parseInput<N extends CallableName>(name: N, data: unknown): InputOf<N> {
  const payload = (typeof data === "object" && data !== null ? data : {}) as Record<
    string,
    unknown
  >;
  const schema = CALLABLES[name].input as Record<string, Field<unknown>>;
  const parsed: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(schema)) {
    const value = payload[key];
    if (value === undefined || value === null) {
      if (!field.optional) {
        field.parse(value, key);
      }
      continue;
    }
    parsed[key] = field.parse(value, key);
  }
  return parsed as InputOf<N>;
}

/* ------------------------------------------------------------ access policy */

/** The custom claims on an ID token: `appRole`, `orgId`, `buildingId`, `setup`. */
export type Token = Readonly<Record<string, unknown>>;

/**
 * Why a call was refused before its work began. The functions send it as the
 * error's `details.reason`, and each app turns it into its own words, in its own
 * languages, instead of showing the function's English sentence.
 */
export const REFUSAL_REASONS = [
  "signIn",
  "notAllowed",
  "setup",
  "noBuilding",
  "suspended",
  "notLive",
  "notInPlan",
  "invalid",
] as const;
export type RefusalReason = (typeof REFUSAL_REASONS)[number];

export type Refusal = {
  code: "unauthenticated" | "permission-denied" | "failed-precondition";
  reason: RefusalReason;
  message: string;
};

/** The reason on a callable error's details, if it carries one this contract knows. */
export function refusalReasonOf(details: unknown): RefusalReason | null {
  const reason = (details as { reason?: unknown } | null | undefined)?.reason;
  return REFUSAL_REASONS.find((known) => known === reason) ?? null;
}

/**
 * Whether a caller may make a call, before the complex's status is known.
 * Returns what the status must be for the call to go ahead, or a refusal.
 */
export function checkAccess(
  access: Access,
  token: Token | null,
  orgId: string | undefined,
): { needsLiveOrg: boolean } | Refusal {
  if (!token) {
    return { code: "unauthenticated", reason: "signIn", message: "Sign in first." };
  }
  const admin = token.appRole === "platform_admin";
  if (access === "self") {
    return { needsLiveOrg: false };
  }
  if (access === "platformAdmin") {
    return admin
      ? { needsLiveOrg: false }
      : {
          code: "permission-denied",
          reason: "notAllowed",
          message: "Only a platform administrator may do that.",
        };
  }
  if ((access === "staff" || access === "support") && admin) {
    return { needsLiveOrg: false };
  }
  const role =
    access === "committee" || (access === "support" && token.appRole === "committee")
      ? "committee"
      : access === "resident"
        ? "resident"
        : "accountant";
  if (token.appRole !== role || token.orgId !== orgId) {
    return {
      code: "permission-denied",
      reason: "notAllowed",
      message: `Only this organization's ${role} may do that.`,
    };
  }
  // The rules refuse an account still on its one-time code; a callable writes
  // with admin credentials, so it refuses the same caller itself.
  if (token.setup === true) {
    return {
      code: "permission-denied",
      reason: "setup",
      message: "Finish setting up this account first.",
    };
  }
  if (
    role === "resident" &&
    (typeof token.unitId !== "string" ||
      !token.unitId ||
      typeof token.termId !== "string" ||
      !token.termId)
  ) {
    return {
      code: "permission-denied",
      reason: "notAllowed",
      message: "This account is not attached to a unit.",
    };
  }
  if (role === "committee" && (typeof token.buildingId !== "string" || !token.buildingId)) {
    return {
      code: "failed-precondition",
      reason: "noBuilding",
      message: "This account is not attached to a building.",
    };
  }
  return { needsLiveOrg: true };
}

/** The second half: a complex that is not live is closed to its members. */
export function checkOrganizationStatus(status: unknown): Refusal | null {
  if (status === "active") {
    return null;
  }
  return status === "suspended"
    ? {
        code: "failed-precondition",
        reason: "suspended",
        message: "This complex's account is suspended.",
      }
    : {
        code: "failed-precondition",
        reason: "notLive",
        message: "This complex is not active yet.",
      };
}

/**
 * The third half, for a call that is one of a plan's extras: refused when the
 * complex's plan does not include it. Read from the same document as the status.
 */
export function checkEntitlement(
  name: CallableName,
  organization: Record<string, unknown> | undefined,
): Refusal | null {
  const spec = CALLABLES[name];
  const needs = "needs" in spec ? (spec.needs as Entitlement) : undefined;
  if (needs === undefined || entitlementsOf(organization).has(needs)) {
    return null;
  }
  return {
    code: "failed-precondition",
    reason: "notInPlan",
    message: "This complex's plan does not include that.",
  };
}

/** Whether a signed-in app should offer a call at all, from the token alone. */
export function mayCall(name: CallableName, token: Token | null, orgId?: string): boolean {
  return !("code" in checkAccess(CALLABLES[name].access, token, orgId));
}

export function isOrgScoped(name: CallableName): boolean {
  return ORG_SCOPED.includes(CALLABLES[name].access);
}

/* ---------------------------------------------------------------- outputs */

export interface NewAccount {
  uid: string;
  loginId: string;
  /** The one-time password for the slip. Shown once. */
  password: string;
}

export interface IssuedSlip {
  unitId: string;
  /** Null when there is nothing to print: no account yet, or already set up. */
  password: string | null;
  status: "issued" | "setUp" | "noAccount";
}

/** What the apps read back. Anything not listed returns nothing they rely on. */
export interface Outputs {
  createComplexCall: { orgId: string; buildingId: string };
  createOrganizationCall: { orgId: string; buildingId: string };
  setOrganizationStatusCall: { orgId: string; status: string };
  createStaffCall: NewAccount;
  createResidentCall: NewAccount;
  resetPasswordCall: { loginId: string; password: string };
  issueSlipsCall: IssuedSlip[];
  vouchForResidentCall: { unitId: string; uid: string };
  addUnitsCall: { added: string[]; existing: string[] };
  handOverUnitCall: { unitId: string; termId: string; carriedBalance: number; password: string };
  savePlanCall: { planId: string };
  startTelebirrPaymentCall: { orderId: string; checkoutUrl: string };
  checkTelebirrPaymentCall: { status: TelebirrOrderStatus };
  setTelebirrMerchantCall: { enabled: boolean };
  startChapaPaymentCall: { orderId: string; checkoutUrl: string };
  checkChapaPaymentCall: { status: ChapaOrderStatus };
  chapaReceiptCall: ChapaReceiptDetail;
  setChapaSubaccountCall: { enabled: boolean };
  setSubscriptionCall: { orgId: string; planId: string };
}

export type OutputOf<N extends CallableName> = N extends keyof Outputs ? Outputs[N] : unknown;
